import type { TFunction } from 'i18next';

/** GERP PR(제상품)(122) — 레거시 appr_edit_forms_po_order_select_choice_mstr */
export const PURCHASE_REQUISITION_TYPE = 122;

/** GERP PO(합의납기일/생산처)(129) — 122 와 선택·카드·부분반려가 같아 같은 코드로. 레거시 appr_edit_forms_po_order_select_choice_cacp_mstr */
export const PO_CHANGE_TYPE = 129;

/** 122·129 공통 */
export const isPurchaseRequisitionType = (apvType: number): boolean =>
  apvType === PURCHASE_REQUISITION_TYPE || apvType === PO_CHANGE_TYPE;

export const isPoChangeType = (apvType: number): boolean =>
  apvType === PO_CHANGE_TYPE;

export const PURCHASE_REQUISITION_MISSING_MESSAGE =
  '데이터를 로드하지 않았습니다. 조회 조건 선택 후 확인 버튼을 누르세요.';

/** 부분 반려 사유 상한 — 백엔드 검증과 같게 */
export const PURCHASE_REQUISITION_REJECT_REASON_MAX_LENGTH = 300;

/** GERP 미상신 결재 건 — 키는 GERP 응답 그대로 */
export type PurchaseRequisitionCandidate = {
  sysId: string;
  aprvId: string;
  aprvTypcd: string;
  title?: string | null;
  docTit?: string | null;
  prId?: string | null;
};

type Value = string | number | null | undefined;

export type PurchaseRequisitionPartcode = {
  aprvId?: string | null;
  partcode?: Value;
  styleNo?: Value;
};

export type PurchaseRequisitionCardHeader = {
  brand?: Value;
  brandName?: Value;
  styleNo?: Value;
  styleName?: Value;
  sex?: Value;
  familyStyleNo?: Value;
  tempCost?: Value;
  typeName?: Value;
  clothesGroup?: Value;
  requestFinish?: Value;
  mainFinishSched?: Value;
  poCountry?: Value;
  manufacturerName?: Value;
  imageName?: Value;
  cardNo?: Value;
  seasonRun?: Value;
  /** 129 만 — 변경 전 합의납기·생산처 */
  beforeMainFinishSched?: Value;
  beforeManufacturerName?: Value;
};

export type PurchaseRequisitionDetail = {
  teamCode?: Value;
  color?: Value;
  colorName?: Value;
  colorCode?: Value;
  measure?: Value;
  qty?: Value;
  total?: Value;
};

/** GERP 122 본문 카드 1장 — 키는 GERP 응답 그대로 */
export type PurchaseRequisitionCardData = {
  aprvId?: string | null;
  prId?: string | null;
  header?: PurchaseRequisitionCardHeader | null;
  sizes?: { sizCount?: Value; color?: Value } | null;
  details?: PurchaseRequisitionDetail[] | null;
};

export type PurchaseRequisitionContent = {
  partcodes?: PurchaseRequisitionPartcode[] | null;
  cards?: PurchaseRequisitionCardData[] | null;
};

export type PurchaseRequisitionDraftSource = {
  code: number;
  attributes: Record<string, unknown>;
  display: {
    candidates: PurchaseRequisitionCandidate[] | null;
    selection: string[] | null;
    subject: string | null;
    content: PurchaseRequisitionContent | null;
  };
};

/** 작성 중 본문에 추가한 카드 */
export type PurchaseRequisitionCard = {
  aprvId: string;
  styleNo: string;
  cardNo: string;
  /** 작성 시점 카드 HTML */
  html: string;
};

export type PurchaseRequisitionState = {
  systemId: string;
  approvalTypeCode: string;
  approvalIds: string[];
  /** 새로 작성할 때만. 저장 문서를 편집할 때는 비어 있고 content 만 */
  cards: PurchaseRequisitionCard[];
  /** 작성 시점 본문 HTML(카드 전체) */
  content: string | null;
};

const text = (v: unknown): string =>
  v === null || v === undefined ? '' : String(v).trim();

export const parsePurchaseRequisitionAttributes = (
  attributes: Record<string, unknown> | null | undefined,
): PurchaseRequisitionState | null => {
  const systemId = text(attributes?.systemId);
  const approvalTypeCode = text(attributes?.approvalTypeCode);
  const approvalIds = text(attributes?.approvalId)
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean);
  if (!systemId || !approvalTypeCode || !approvalIds.length) return null;
  return {
    systemId,
    approvalTypeCode,
    approvalIds,
    cards: [],
    content: text(attributes?.content) || null,
  };
};

export const parsePurchaseRequisitionContent = (
  attributes: Record<string, unknown> | null | undefined,
): string | null => text(attributes?.content) || null;

export const buildPurchaseRequisitionAttributes = (
  state: PurchaseRequisitionState | null | undefined,
) =>
  state?.approvalIds.length
    ? {
        systemId: state.systemId,
        approvalTypeCode: state.approvalTypeCode,
        approvalId: state.approvalIds.join(','),
        content: state.content,
      }
    : null;

/** 카드 목록이 바뀔 때마다 결재 건·본문을 다시 맞춤 */
export const withPurchaseRequisitionCards = (
  base: Pick<PurchaseRequisitionState, 'systemId' | 'approvalTypeCode'>,
  cards: PurchaseRequisitionCard[],
): PurchaseRequisitionState | null =>
  cards.length
    ? {
        ...base,
        approvalIds: cards.map((c) => c.aprvId),
        cards,
        content: cards.map((c) => c.html).join(''),
      }
    : null;

export const checkPurchaseRequisitionBeforeSave = (
  state: PurchaseRequisitionState | null | undefined,
): string | null =>
  state?.approvalIds.length && state.content
    ? null
    : PURCHASE_REQUISITION_MISSING_MESSAGE;

export const purchaseRequisitionKey = (
  state: PurchaseRequisitionState | null | undefined,
) => state?.approvalIds.join(',') ?? '';

/** 레거시 콤보 문구 — TITLE */
export const buildPurchaseRequisitionCandidateLabel = (
  c: PurchaseRequisitionCandidate,
) => c.title?.trim() || c.docTit?.trim() || c.aprvId;

/** 레거시 change_title_content — 고른 순서의 첫 품번 기준, 0건이면 빈 제목 */
export const buildPurchaseRequisitionSubject = (
  styleNos: string[],
  t: TFunction,
  apvType: number = PURCHASE_REQUISITION_TYPE,
): string => {
  if (!styleNos.length) return '';
  const isPo = isPoChangeType(apvType);
  if (styleNos.length === 1)
    return isPo
      ? `Purchase Order(${styleNos[0]})`
      : `Purchase Requisition(${styleNos[0]})`;
  const options = { styleNo: styleNos[0], count: styleNos.length - 1 };
  return isPo
    ? t('Purchase Order({{styleNo}}외 {{count}}건)', options)
    : t('Purchase Requisition({{styleNo}}외 {{count}}건)', options);
};

/** 레거시는 체크할 때마다 본문 에디터를 이 문구로 덮어씀 */
export const buildPurchaseRequisitionEditorHtml = (
  styleNos: string[],
  t: TFunction,
): string =>
  `<p>${escapeHtml(
    t('추가품번 : {{styleNos}}', { styleNos: styleNos.join(',') }),
  )}</p>`;

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

export const getPurchaseRequisitionStyleNo = (
  card: PurchaseRequisitionCardData,
  partcodes: PurchaseRequisitionPartcode[] | null | undefined,
): string => {
  const partcode = partcodes?.find((p) => p.aprvId === card.aprvId);
  return (
    text(card.header?.styleNo) ||
    text(partcode?.styleNo) ||
    text(partcode?.partcode)
  );
};

const PLM_IMAGE_BASE_URL =
  'https://s3.ap-northeast-2.amazonaws.com/static-plm.fnf.co.kr/';

/** 레거시는 PLM S3 주소 + 이미지 경로. GERP 가 전체 주소를 주면 그대로 */
export const resolvePurchaseRequisitionImageUrl = (v: Value): string => {
  const path = text(v);
  if (!path) return '';
  if (/^(https?:)?\/\//i.test(path)) return path;
  return PLM_IMAGE_BASE_URL + path.replace(/^\/+/, '');
};

/** 레거시 사이즈 개수별 표 묶음. 17개가 넘으면 16·17 처럼 4개씩 */
const LEGACY_SIZE_GROUPS: Record<number, number[]> = {
  10: [6, 4],
  11: [7, 4],
  12: [7, 5],
  13: [7, 6],
  14: [8, 6],
  15: [9, 6],
};

export const chunkPurchaseRequisitionSizes = (sizes: string[]): string[][] => {
  const n = sizes.length;
  if (!n) return [];
  const counts =
    n <= 9
      ? [n]
      : LEGACY_SIZE_GROUPS[n] ??
        Array.from({ length: Math.ceil(n / 4) }, (_, i) =>
          Math.min(4, n - i * 4),
        );
  let start = 0;
  return counts.map((count) => {
    const group = sizes.slice(start, start + count);
    start += count;
    return group;
  });
};

export type PurchaseRequisitionRow = {
  teamCode: string;
  color: string;
  colorName: string;
  colorCode: string;
  qty: Record<string, number>;
  total: number;
};

const toNumber = (v: Value): number => {
  const n = Number(text(v).replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
};

/** details[] 는 팀·컬러·사이즈별 한 줄 — 팀·컬러 한 줄에 사이즈를 펼침 */
export const buildPurchaseRequisitionRows = (
  details: PurchaseRequisitionDetail[] | null | undefined,
): PurchaseRequisitionRow[] => {
  const rows = new Map<string, PurchaseRequisitionRow>();
  (details ?? []).forEach((d) => {
    const key = `${text(d.teamCode)}|${text(d.color)}`;
    let row = rows.get(key);
    if (!row) {
      row = {
        teamCode: text(d.teamCode),
        color: text(d.color),
        colorName: text(d.colorName),
        colorCode: text(d.colorCode),
        qty: {},
        total: toNumber(d.total),
      };
      rows.set(key, row);
    }
    const measure = text(d.measure).replace(/\s/g, '');
    if (measure) row.qty[measure] = (row.qty[measure] ?? 0) + toNumber(d.qty);
  });
  return Array.from(rows.values());
};

/** sizes.color 는 'S|M|L|'. 중복은 한 번만, 수량에만 있는 사이즈는 뒤에 붙여 수량이 빠지지 않게 */
export const getPurchaseRequisitionSizes = (
  card: PurchaseRequisitionCardData,
): string[] => {
  const sizes: string[] = [];
  const push = (v: Value) => {
    const size = text(v).replace(/\s/g, '');
    if (size && !sizes.includes(size)) sizes.push(size);
  };
  text(card.sizes?.color).split('|').forEach(push);
  (card.details ?? []).forEach((d) => push(d.measure));
  return sizes;
};
